import axios from "axios";

// Todas las llamadas axios del visor de parque solar (solar-park), centralizadas.
// Devuelven la promesa de axios tal cual (la respuesta completa) para que los
// consumidores conserven su manejo actual: .then(response => response.data...) / .catch.

// ============================ Power plant ============================

// Carga el power plant del proyecto (entityType 'project') o uno concreto por id.
export const getPowerPlant = (entityId, entityType = 'project', params = {}) => {
  const url = entityType === 'project'
    ? `/api/v2/project/${entityId}/power-plant`
    : `/api/v2/power-plant/${entityId}`;
  return axios.get(url, { params });
};

// Descarga el JSON (comprimido) del diseño de un pictograma.
export const getPictogramJson = (pictogramId) =>
  axios.get(`/api/v2/pictogram/${pictogramId}/json`, { params: { compressed: 1 } });

// Pictogramas del proyecto (actividad asociada + opcionalmente screenshot/power plant).
// El screenshot es CARO (lee S3 por pictograma); pásalo a false cuando no haga falta la
// imagen (p.ej. el panel de info, que no la usa) para que la consulta sea rápida.
export const getProjectPictograms = (projectId, { withScreenshot = true, withPowerPlant = true } = {}) =>
  axios.get(`/api/v2/project/${projectId}/pictograms`, {
    params: {
      with_associated_activity: 1,
      ...(withScreenshot ? { with_screenshot: 1 } : {}),
      ...(withPowerPlant ? { with_power_plant: 1 } : {}),
    },
  });

// Serie temporal (curva S) por grupo raíz del proyecto: programado/ejecutado/real/previsto
// por mes. `data.data` = [{id, name, data:[{name,data:{fecha:valor}}], withActualProgress,...}].
export const getPhotovoltaicPanel3 = (projectId, params = {}) =>
  axios.get(`/api/v2/project/${projectId}/dashboard/photovoltaicpanel3`, { params });

// Descarga el binario .epcp de una vista del parque como ArrayBuffer (mismo origen,
// vía nuestra API que lo lee de S3). Responde 404 si el parque no tiene binario
// generado para esa vista → el visor cae al camino JSON.
export const getViewBinary = (powerPlantId, view) =>
  axios.get(`/api/v2/power-plant/${powerPlantId}/binary/${view}`, { responseType: 'arraybuffer' });

// Estado por entidad + color de una vista (el estado NO va en el binario; viene en
// vivo de Mongo). Devuelve { statuses: {"picId:entityId": statusId}, colors: {statusId: color} }.
export const getViewStatuses = (powerPlantId, view) =>
  axios.get(`/api/v2/power-plant/${powerPlantId}/statuses`, { params: { view } });

// Nombres (etiquetas) de las entidades de UN pictograma (se piden al enfocar un bloque).
export const getPictogramNames = (powerPlantId, pictogramId, view) =>
  axios.get(`/api/v2/power-plant/${powerPlantId}/names`, { params: { pictogram: pictogramId, view } });

// Info de UN elemento (componente) de un pictograma: historial de progreso/incidencias,
// estado actual, atributos extra, metadata (code/name) y certificación. `workLayer` es la
// capa (pilings/structures/modules). Devuelve { success, component: {...} }.
export const getPictogramComponent = (pictogramId, componentId, workLayer) =>
  axios.get(`/api/v2/pictogram/${pictogramId}/component/${componentId}`, { params: { work_lyr: workLayer } });

// Guarda el power plant: crea (project/{id}/power-plant) si no hay id, o edita
// (power-plant/{id}) si ya existe. `form` es un FormData.
export const savePowerPlant = (entityId, form, powerPlantId = null) => {
  const url = powerPlantId
    ? `/api/v2/power-plant/${powerPlantId}`
    : `/api/v2/project/${entityId}/power-plant`;
  return axios.post(url, form);
};

// Guarda solo la captura (screenshot) del power plant. `form` lleva update_only_screenshot.
export const savePowerPlantScreenshot = (powerPlantId, form) =>
  axios.post(`/api/v2/power-plant/${powerPlantId}`, form);

// ============================ Progreso ==============================

export const getPowerPlantProgress = (projectId) =>
  axios.get(`/api/v2/project/${projectId}/power-plant-progress`, { params: {} });

export const getPictogramProgress = (pictogramId) =>
  axios.get(`/api/v2/pictograms/${pictogramId}/progress`, { params: {} });

export const getPictogramProgressDataset = (pictogramId) =>
  axios.get(`/api/v2/pictogram/${pictogramId}/progress-dataset`, { params: {} });
